import type { TreeNode } from "primereact/treenode";
import { EXPORT_DATASET_KEYS } from "~/features/entity-export/constants";
import type { ExportDatasetKey } from "~/features/entity-export/constants";

/**
 * PrimeReact Tree selection state keyed by node key.
 * Each entry tracks whether that node is fully checked or partially checked
 * (i.e. some but not all children selected).
 */
export type TreeSelectionKeys = Record<
  string,
  { checked?: boolean; partialChecked?: boolean }
>;

/**
 * Builds the hierarchical tree structure for the dataset selection checklist
 * in the export modal. The tree has up to 3 nesting levels:
 *   Level 1 - dataset category (e.g. "Directors")
 *   Level 2 - sub-group (e.g. "Roles", "Statuses", "Data to Include")
 *   Level 3 - leaf options (e.g. "Executive Directors", "Address")
 *
 * Entity Plus-gated datasets (PoA, Officers, and the "Entity Plus Details"
 * sub-node under Core Entity) are only included when {@link showEntityPlus}
 * is true. The gating decision is made per-entity in the modal via
 * {@link hasEntityPlus}.
 *
 * Key naming convention uses double underscores as level separators:
 *   `{dataset}__{group}__{leaf}` (e.g. `directors__data__address`).
 * Only top-level keys (e.g. `directors`, `core_entity`) are sent to the
 * backend - the sub-keys are purely for UI granularity.
 *
 * @param showEntityPlus - Whether the entity has Entity+ service enabled
 * @returns Array of PrimeReact TreeNode objects for the dataset checklist
 */
export function buildDatasetTreeNodes(showEntityPlus: boolean): TreeNode[] {
  const nodes: TreeNode[] = [
    {
      key: "core_entity",
      label: "Core Entity Information",
      children: [
        { key: "core_entity__general", label: "General Information" },
        ...(showEntityPlus
          ? [
              {
                key: "core_entity__entity_plus",
                label: "Entity Plus Details",
              },
            ]
          : []),
        { key: "core_entity__addresses", label: "Addresses" },
        { key: "core_entity__key_personnel", label: "Key Personnel" },
      ],
    },
    {
      key: "directors",
      label: "Directors",
      children: [
        {
          key: "directors__roles",
          label: "Roles",
          children: [
            {
              key: "directors__roles__executive",
              label: "Executive Directors",
            },
            {
              key: "directors__roles__non_executive",
              label: "Non-Executive Directors",
            },
            {
              key: "directors__roles__managing",
              label: "Managing Directors",
            },
          ],
        },
        {
          key: "directors__statuses",
          label: "Statuses",
          children: [
            {
              key: "directors__statuses__active",
              label: "Active Directors",
            },
            {
              key: "directors__statuses__not_active",
              label: "Not Active Directors",
            },
            {
              key: "directors__statuses__removed",
              label: "Removed Directors",
            },
          ],
        },
        {
          key: "directors__data",
          label: "Data to Include",
          children: [
            {
              key: "directors__data__director_info",
              label: "Director Information",
            },
            {
              key: "directors__data__identification",
              label: "Identification",
            },
            { key: "directors__data__address", label: "Address" },
            {
              key: "directors__data__appointment_history",
              label: "Appointment History",
            },
          ],
        },
      ],
    },
    {
      key: "shareholders",
      label: "Shareholders",
      children: [
        {
          key: "shareholders__type",
          label: "Type",
          children: [
            { key: "shareholders__type__corporate", label: "Corporate" },
            { key: "shareholders__type__individual", label: "Individual" },
          ],
        },
        {
          key: "shareholders__status",
          label: "Status",
          children: [
            { key: "shareholders__status__active", label: "Active" },
            { key: "shareholders__status__inactive", label: "Inactive" },
          ],
        },
        {
          key: "shareholders__data",
          label: "Data to Include",
          children: [
            {
              key: "shareholders__data__info",
              label: "Shareholder Information",
            },
            { key: "shareholders__data__address", label: "Address" },
            {
              key: "shareholders__data__share_registry",
              label: "Share Registry",
            },
          ],
        },
      ],
    },
  ];

  // PoA and Officers are Entity Plus-only datasets (GXM-1879).
  // They are appended rather than conditionally spread so the base 3
  // datasets always occupy stable array positions.
  if (showEntityPlus) {
    nodes.push(
      {
        key: "poa",
        label: "Power of Attorney (PoAs)",
        children: [
          {
            key: "poa__role",
            label: "Role",
            children: [
              {
                key: "poa__role__president_ceo",
                label: "President and CEO",
              },
              { key: "poa__role__vp", label: "VP" },
              { key: "poa__role__secretary", label: "Secretary" },
              { key: "poa__role__cfo", label: "CFO" },
              { key: "poa__role__board_member", label: "Board Member" },
            ],
          },
          { key: "poa__personal_info", label: "Personal Information" },
        ],
      },
      {
        key: "officers",
        label: "Officers",
        children: [
          {
            key: "officers__role",
            label: "Role",
            children: [
              {
                key: "officers__role__president_ceo",
                label: "President and CEO",
              },
              { key: "officers__role__vp", label: "VP" },
              { key: "officers__role__secretary", label: "Secretary" },
              { key: "officers__role__cfo", label: "CFO" },
              { key: "officers__role__board_member", label: "Board Member" },
            ],
          },
          { key: "officers__personal_info", label: "Personal Information" },
        ],
      },
    );
  }

  return nodes;
}

/**
 * Extracts the top-level dataset keys that the user has selected (fully or
 * partially) from the PrimeReact Tree selection state.
 *
 * Only keys that exist in {@link EXPORT_DATASET_KEYS} are returned - nested
 * sub-keys (e.g. `directors__roles__executive`) are ignored because the
 * backend only accepts top-level dataset identifiers.
 *
 * @param selectionKeys - Current tree checkbox state, or null if nothing selected
 * @returns Array of backend-compatible dataset keys (e.g. `["core_entity", "directors"]`)
 */
export function treeSelectionToDatasetKeys(
  selectionKeys: TreeSelectionKeys | null,
): ExportDatasetKey[] {
  if (!selectionKeys) return [];
  // Filter against the canonical key list so only backend-valid keys pass through
  return EXPORT_DATASET_KEYS.filter((key) => {
    const state = selectionKeys[key];
    return state?.checked || state?.partialChecked;
  });
}

/**
 * Counts the number of leaf nodes (nodes with no children) that are checked
 * in the current tree selection. Used to display a count badge on each
 * entity card's "Information Available" toggle (e.g. "Information Available (5)").
 *
 * @param selectionKeys - Current tree checkbox state, or null if nothing selected
 * @param nodes - The full tree node structure to walk
 * @returns Number of checked leaf nodes
 */
export function countSelectedLeaves(
  selectionKeys: TreeSelectionKeys | null,
  nodes: TreeNode[],
): number {
  if (!selectionKeys) return 0;
  let count = 0;
  function walk(node: TreeNode): void {
    // Leaf node: no children - check if it's selected
    if (!node.children || node.children.length === 0) {
      if (selectionKeys![node.key as string]?.checked) count++;
    } else {
      node.children.forEach(walk);
    }
  }
  nodes.forEach(walk);
  return count;
}
